import {
  addPlacePhoto,
  addPlaceTag,
  changePlace,
  changePlaceTag,
  ConfirmDialog,
  getPlaceMore,
  getPlaces,
  keepPlacesApart,
  mergePlaces,
  putPlaceOnTrip,
  removePlace,
  removePlacePhoto,
  removePlaceTag,
  toIso,
  type OwnPlace,
  type PlaceChange,
  type PlaceLibrary,
  type PlaceMore,
  type PlaceTag,
} from '@trippath/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MapPanel } from '../../components/map/MapPanel';
import type { MapData } from '../../lib/map';
import { countryName, day, PlaceDetail } from './PlaceDetail';
import { EditDialog, TagDialog, TagsDialog } from './PlaceDialogs';

type State =
  { kind: 'loading' } | { kind: 'failed' } | { kind: 'ready'; library: PlaceLibrary };

/** A dialog open: a tag made or changed, a place's tags, the place edited or let go of. */
type Open =
  | { kind: 'tag'; tag: PlaceTag | null }
  | { kind: 'tags' }
  | { kind: 'edit' }
  | { kind: 'remove' }
  | null;

/** The emoji and colour a place is drawn with: its first tag's, else a plain pin. */
function looks(place: OwnPlace, tags: PlaceTag[]): { emoji: string; colour: string } {
  const tag = tags.find((one) => place.tags.includes(one.id));
  return tag ? { emoji: tag.emoji, colour: tag.colour } : { emoji: '📍', colour: '#475569' };
}

/**
 * One's own places (the `Places` mockup): kept apart from any trip, on a list and a map — found
 * by name, town or tag, by tag, favourites only, those been to shown or not — and the one chosen
 * beside them.
 */
export function MyPlaces() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [chosen, setChosen] = useState<string | null>(null);
  const [more, setMore] = useState<{ id: string; more: PlaceMore } | null>(null);
  const [query, setQuery] = useState('');
  const [tagged, setTagged] = useState<string | null>(null);
  const [favourites, setFavourites] = useState(false);
  const [visited, setVisited] = useState(true);
  const [open, setOpen] = useState<Open>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const load = useCallback(
    () =>
      getPlaces().then(
        (library) => {
          setState({ kind: 'ready', library });
          setChosen((was) =>
            was !== null && library.places.some((place) => place.id === was)
              ? was
              : (library.places[0]?.id ?? null),
          );
        },
        () => {
          setState({ kind: 'failed' });
        },
      ),
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);

  // What OpenStreetMap says of the place chosen, and the stops near it: asked each time it is.
  useEffect(() => {
    if (chosen === null) return;
    let live = true;
    getPlaceMore(chosen).then(
      (said) => {
        if (live) setMore({ id: chosen, more: said });
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [chosen]);

  const library = state.kind === 'ready' ? state.library : null;
  const tags = useMemo(() => library?.tags ?? [], [library]);
  const places = useMemo(() => library?.places ?? [], [library]);
  const country = useCallback(
    (place: OwnPlace) => (place.country === null ? null : countryName(place.country, lang)),
    [lang],
  );
  const shown = useMemo(() => {
    const words = query.trim().toLowerCase();
    return places.filter((place) => {
      if (tagged !== null && !place.tags.includes(tagged)) return false;
      if (favourites && !place.favourite) return false;
      if (!visited && place.visitedOn !== null) return false;
      if (words === '') return true;
      const names = tags.filter((tag) => place.tags.includes(tag.id)).map((tag) => tag.name);
      return [place.name, place.address, place.city, country(place), ...names]
        .filter((one) => one !== null)
        .some((one) => one.toLowerCase().includes(words));
    });
  }, [places, tags, query, tagged, favourites, visited, country]);
  const map: MapData = useMemo(
    () => ({
      points: [],
      lines: [],
      pins: shown.map((place) => ({
        key: place.id,
        label: place.name,
        lat: place.lat,
        lon: place.lon,
        faded: place.visitedOn !== null,
        chosen: place.id === chosen,
        ...looks(place, tags),
      })),
    }),
    [shown, tags, chosen],
  );

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind === 'failed') {
    return (
      <p className="page form-failed" role="alert">
        {t('myPlaces.failed')}
      </p>
    );
  }
  const place = places.find((one) => one.id === chosen) ?? null;
  const editing = tags.find((tag) => tag.id === tagged);
  const countries = new Set(
    places.flatMap((one) => (one.country === null ? [] : [one.country])),
  );
  const said = more !== null && more.id === chosen ? more.more : null;

  /** Does `job`, then reads the library again; what failed said. */
  const act = (job: () => Promise<unknown>) => {
    setBusy(true);
    setFailed(null);
    job()
      .then(load, (error: unknown) => {
        setFailed(
          error instanceof Error && error.message === 'photo'
            ? t('myPlaces.photoFailed')
            : t('myPlaces.failed'),
        );
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const change = (id: string, what: PlaceChange) => {
    act(() => changePlace(id, what));
  };

  return (
    <div className="places-page">
      <aside className="places-left" aria-label={t('myPlaces.title')}>
        <div className="places-sec">
          <div className="places-head">
            <h1>{t('myPlaces.title')}</h1>
            <span className="places-small quiet">
              {t('myPlaces.count', { count: places.length })} ·{' '}
              {t('myPlaces.countries', { count: countries.size })}
            </span>
          </div>
          <p className="places-small quiet">{t('myPlaces.lead')}</p>
          <input
            type="search"
            className="text-input"
            placeholder={t('myPlaces.search')}
            aria-label={t('myPlaces.search')}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
        </div>
        <div className="places-sec">
          <span className="places-caps">{t('myPlaces.tags')}</span>
          <div className="places-filters" role="group" aria-label={t('myPlaces.tags')}>
            <button
              type="button"
              className="places-ft"
              aria-pressed={tagged === null}
              onClick={() => {
                setTagged(null);
              }}
            >
              {t('myPlaces.all')} <span className="places-c">{places.length}</span>
            </button>
            {tags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                className="places-ft"
                aria-pressed={tagged === tag.id}
                onClick={() => {
                  setTagged(tagged === tag.id ? null : tag.id);
                }}
              >
                <span className="places-dot" style={{ background: tag.colour }} />
                {tag.emoji} {tag.name} <span className="places-c">{tag.places}</span>
              </button>
            ))}
            <button
              type="button"
              className="link-button places-small"
              onClick={() => {
                setOpen({ kind: 'tag', tag: null });
              }}
            >
              + {t('myPlaces.newTag')}
            </button>
            {editing !== undefined && (
              <button
                type="button"
                className="link-button places-small"
                onClick={() => {
                  setOpen({ kind: 'tag', tag: editing });
                }}
              >
                {t('myPlaces.tagEditTitle')}
              </button>
            )}
          </div>
          <div className="places-filters">
            <button
              type="button"
              className="places-ft"
              aria-pressed={favourites}
              onClick={() => {
                setFavourites(!favourites);
              }}
            >
              ★ {t('myPlaces.favouritesOnly')}
            </button>
            <button
              type="button"
              className="places-ft"
              aria-pressed={visited}
              onClick={() => {
                setVisited(!visited);
              }}
            >
              ✓ {t('myPlaces.showVisited')}
            </button>
          </div>
        </div>
        {places.length === 0 ? (
          <p className="places-sec quiet">{t('myPlaces.none')}</p>
        ) : shown.length === 0 ? (
          <p className="places-sec quiet">{t('myPlaces.noneFound')}</p>
        ) : (
          <ul className="places-list">
            {shown.map((one) => {
              const look = looks(one, tags);
              const where = one.city ?? one.address ?? '';
              return (
                <li key={one.id}>
                  <button
                    type="button"
                    className={`places-pl${one.visitedOn === null ? '' : ' places-done'}`}
                    aria-current={one.id === chosen}
                    onClick={() => {
                      setChosen(one.id);
                    }}
                  >
                    <span className="places-em" style={{ borderColor: look.colour }}>
                      {look.emoji}
                    </span>
                    <span>
                      <b>{one.name}</b>
                      <small>
                        {one.visitedOn === null
                          ? [where, country(one)]
                              .filter((part) => part !== null && part !== '')
                              .join(' · ')
                          : [where, t('myPlaces.visitedOn', { day: day(one.visitedOn, lang) })]
                              .filter((part) => part !== '')
                              .join(' · ')}
                      </small>
                    </span>
                    <span className="places-marks">
                      {one.favourite && (
                        <span className="places-star" aria-label={t('myPlaces.favourite')}>
                          ★
                        </span>
                      )}
                      {one.visitedOn !== null && (
                        <span className="places-ok" aria-label={t('myPlaces.visited')}>
                          ✓
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </aside>

      <div className="places-map">
        <MapPanel
          data={map}
          label={t('myPlaces.mapLabel')}
          cooperative={false}
          keepView
          closest={13}
          show={place === null ? null : [place]}
          onPoint={setChosen}
        />
        <p className="places-mapnote">{t('myPlaces.mapNote')}</p>
      </div>

      <aside className="places-right" aria-label={t('myPlaces.place')}>
        {failed !== null && (
          <p className="form-failed places-sec" role="alert">
            {failed}
          </p>
        )}
        {place === null ? (
          <p className="places-sec quiet">{t('myPlaces.noneChosen')}</p>
        ) : (
          <PlaceDetail
            place={place}
            tags={tags}
            more={said}
            busy={busy}
            onFavourite={() => {
              change(place.id, { favourite: !place.favourite });
            }}
            onVisited={() => {
              change(place.id, {
                visitedOn: place.visitedOn === null ? toIso(new Date()) : null,
              });
            }}
            onTags={() => {
              setOpen({ kind: 'tags' });
            }}
            onEdit={() => {
              setOpen({ kind: 'edit' });
            }}
            onPut={(stop) => {
              act(() => putPlaceOnTrip(place.id, stop.trip, stop.stop));
            }}
            onMerge={(other) => {
              act(() => mergePlaces(place.id, other));
            }}
            onApart={(other) => {
              act(() => keepPlacesApart(place.id, other));
            }}
            onPhotos={(files) => {
              act(async () => {
                for (const file of files) {
                  await addPlacePhoto(place.id, file).catch(() => {
                    throw new Error('photo');
                  });
                }
              });
            }}
          />
        )}
      </aside>

      {open?.kind === 'tag' && (
        <TagDialog
          tag={open.tag}
          onClose={() => {
            setOpen(null);
          }}
          onRemove={(id) => {
            setOpen(null);
            setTagged(null);
            act(() => removePlaceTag(id));
          }}
          onSave={async (input) => {
            if (open.tag === null) await addPlaceTag(input);
            else await changePlaceTag(open.tag.id, input);
            setOpen(null);
            await load();
          }}
        />
      )}
      {open?.kind === 'tags' && place !== null && (
        <TagsDialog
          place={place}
          tags={tags}
          onClose={() => {
            setOpen(null);
          }}
          onSave={(chosenTags) => {
            setOpen(null);
            change(place.id, { tags: chosenTags });
          }}
        />
      )}
      {open?.kind === 'edit' && place !== null && (
        <EditDialog
          place={place}
          onClose={() => {
            setOpen(null);
          }}
          onSave={(name, note) => {
            setOpen(null);
            change(place.id, { name, note });
          }}
          onRemovePhoto={(photo) => {
            act(() => removePlacePhoto(photo));
          }}
          onRemove={() => {
            setOpen({ kind: 'remove' });
          }}
        />
      )}
      {place !== null && (
        <ConfirmDialog
          open={open?.kind === 'remove'}
          title={t('myPlaces.remove')}
          body={t('myPlaces.removeAsk', { name: place.name })}
          confirmLabel={t('myPlaces.remove')}
          onCancel={() => {
            setOpen(null);
          }}
          onConfirm={() => {
            setOpen(null);
            act(() => removePlace(place.id));
          }}
        />
      )}
    </div>
  );
}
